class EngravingCustomizer {
    constructor(config) {
        this.sectionId = config.sectionId;
        this.productFormId = config.productFormId;
        this.productHandle = config.productHandle;
        this.characterLimit = config.characterLimit;
        this.showPropertiesToCustomer = config.showPropertiesToCustomer;

        this.product = null;
        this.variants = [];

        this.init();
    }

    async init() {
        this.cacheElements();
        await this.loadProduct();
        this.attachEventListeners();
        this.validateForm();
        this.updatePrice();
    }

    cacheElements() {
        const wrapper = document.querySelector(`[data-section-id="${this.sectionId}"]`);
        if (!wrapper) return;

        this.frontTextInput = wrapper.querySelector('[data-engraving-text-front]');
        this.frontFontSelect = wrapper.querySelector('[data-engraving-font-front]');
        this.frontPreview = wrapper.querySelector('[data-engraving-preview-front]');
        this.frontCounter = wrapper.querySelector('[data-engraving-count-front]');

        this.backCheckbox = wrapper.querySelector('[data-engraving-enable-back]');
        this.backContainer = wrapper.querySelector('[data-engraving-container-back]');
        this.backTextInput = wrapper.querySelector('[data-engraving-text-back]');
        this.backFontSelect = wrapper.querySelector('[data-engraving-font-back]');
        this.backPreview = wrapper.querySelector('[data-engraving-preview-back]');
        this.backCounter = wrapper.querySelector('[data-engraving-count-back]');

        this.productForm = document.querySelector(`#${this.productFormId}`);
        this.addToCartButton = this.productForm?.querySelector('button[name="add"]');
        this.priceElement = document.querySelector('.price__regular .price-item--regular');

        this.originalButtonText = this.addToCartButton?.textContent || '';
        
        // Change button type to prevent default form submission
        if (this.addToCartButton) {
            this.addToCartButton.type = 'button';
            this.addToCartButton.setAttribute('data-engraving-button', 'true');
        }
    }

    async loadProduct() {
        try {
            const response = await fetch(`/products/${this.productHandle}.js`);
            this.product = await response.json();
            this.variants = this.product.variants || [];
        } catch (error) {
            console.error('Failed to load product:', error);
        }
    }

    attachEventListeners() {
        if (this.frontTextInput) {
            this.frontTextInput.addEventListener('input', () => this.updatePreview('front'));
        }
        if (this.frontFontSelect) {
            this.frontFontSelect.addEventListener('change', () => this.updatePreview('front'));
        }

        if (this.backCheckbox) {
            this.backCheckbox.addEventListener('change', () => this.handleBackCheckboxChange());
        }

        if (this.backTextInput) {
            this.backTextInput.addEventListener('input', () => this.updatePreview('back'));
        }
        if (this.backFontSelect) {
            this.backFontSelect.addEventListener('change', () => this.updatePreview('back'));
        }

        // Direct button click handler
        if (this.addToCartButton) {
            this.addToCartButton.addEventListener('click', () => {
                this.handleFormSubmit();
            });
        }
    }

    handleBackCheckboxChange() {
        const isChecked = this.backCheckbox.checked;

        if (this.backContainer) {
            this.backContainer.style.display = isChecked ? 'block' : 'none';
        }

        if (!isChecked && this.backTextInput) {
            this.backTextInput.value = '';
            this.updatePreview('back');
        }

        this.updatePrice();
        this.validateForm();
    }

    updatePreview(side) {
        const isFront = side === 'front';
        const textInput = isFront ? this.frontTextInput : this.backTextInput;
        const fontSelect = isFront ? this.frontFontSelect : this.backFontSelect;
        const preview = isFront ? this.frontPreview : this.backPreview;
        const counter = isFront ? this.frontCounter : this.backCounter;

        if (!preview) return;

        const text = textInput?.value || '';
        const font = fontSelect?.value || '';

        if (counter) counter.textContent = text.length;

        if (text) {
            preview.textContent = text;
            preview.style.fontFamily = font ? `'${font}', sans-serif` : '';
        } else {
            const placeholder = preview.getAttribute('data-placeholder') || (isFront ? 'Front side text preview' : 'Back side text preview');
            preview.innerHTML = `<span class="engraving-customizer__preview-placeholder">${placeholder}</span>`;
        }

        this.validateForm();
    }

    updatePrice() {
        if (!this.priceElement || !this.variants.length) return;

        const backEnabled = this.backCheckbox?.checked || false;
        const variant = backEnabled ? this.variants[1] : this.variants[0];
        
        if (!variant) return;

        const formatMoney = (cents) => {
            const price = (cents / 100).toFixed(2);
            return window.Shopify?.formatMoney 
                ? Shopify.formatMoney(cents, window.theme?.moneyFormat || '€{{amount}}')
                : `€${price}`;
        };

        this.priceElement.textContent = formatMoney(variant.price);
    }

    validateForm() {
        if (!this.addToCartButton) return;

        const frontText = this.frontTextInput?.value.trim() || '';
        const backEnabled = this.backCheckbox?.checked || false;
        const backText = this.backTextInput?.value.trim() || '';

        const shouldDisable = !frontText || (backEnabled && !backText);
        this.addToCartButton.disabled = shouldDisable;

        if (shouldDisable) {
            this.addToCartButton.textContent = !frontText ? 'Ievadiet priekšpuses tekstu' : 'Ievadiet aizmugures tekstu';
        } else {
            this.addToCartButton.textContent = this.originalButtonText;
        }
    }

    async handleFormSubmit() {
        const frontText = this.frontTextInput?.value.trim() || '';
        const frontFont = this.frontFontSelect?.value || '';
        const backEnabled = this.backCheckbox?.checked || false;
        const backText = this.backTextInput?.value.trim() || '';
        const backFont = this.backFontSelect?.value || '';

        if (!frontText || (backEnabled && !backText)) return;

        if (this.addToCartButton) {
            this.addToCartButton.disabled = true;
            this.addToCartButton.textContent = 'Pievieno grozamt...';
        }

        try {
            const variant = backEnabled ? this.variants[1] : this.variants[0];
            if (!variant) throw new Error('Variant not found');

            const textKey = this.showPropertiesToCustomer ? 'Gravējuma teksts' : '_Gravējuma teksts';
            const fontKey = this.showPropertiesToCustomer ? 'Gravējuma fonts' : '_Gravējuma fonts';

            const properties = {
                [textKey + ' (Priekšpuse)']: frontText,
                [fontKey + ' (Priekšpuse)']: frontFont
            };

            if (backEnabled) {
                properties[textKey + ' (Aizmugure)'] = backText;
                properties[fontKey + ' (Aizmugure)'] = backFont;
            }

            const quantity = parseInt(this.productForm?.querySelector('[name="quantity"]')?.value || 1);

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variant.id, quantity, properties })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.description || 'Failed to add to cart');
            }

            document.dispatchEvent(new CustomEvent('cart:refresh'));
            
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer?.open) cartDrawer.open();

            this.showSuccessMessage();

        } catch (error) {
            alert(error.message);
            if (this.addToCartButton) {
                this.addToCartButton.disabled = false;
                this.addToCartButton.textContent = this.originalButtonText;
            }
        }
    }

    showSuccessMessage() {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification?.renderContents) {
            fetch('/cart.js')
                .then(r => r.json())
                .then(cart => cartNotification.renderContents(cart));
        }

        if (this.addToCartButton) {
            this.addToCartButton.disabled = false;
            this.addToCartButton.textContent = this.originalButtonText;
        }
    }
}

// Export for global use
if (typeof window !== 'undefined') {
    window.EngravingCustomizer = EngravingCustomizer;
}
